import * as React from 'react';
import { type RuleSet, css, styled } from 'styled-components';

import type { IconGlyph, PriceBoxAlignment, PriceBoxSize } from '@sunwebgroup/ui-components';
import {
  Button,
  Icon,
  PriceBox,
  SpaceBetween,
  Typography,
  tk,
  ButtonLoaderIcon,
  UnavailableContentDialog,
} from '@sunwebgroup/ui-components';

import { usePromotedPrice } from '@/domain/prices/promoted-price/hooks';
import { useIntl } from '@/intl';
import { PriceInfo } from '@/scopes/common/components/PriceInfo';
import { useSendAdditionalDetails } from '@/domain/prices/analytics/useSendAdditionalDetails';
import { useAccommodationAvailability } from '@/domain/prices/availability/hooks';
import { AlertServiceSidebar } from '@/react-pages/Prices/AlertService/AlertServiceSidebar';
import { usePricesStore } from '@/infrastructure/prices/usePricesStore';

interface PromotedPriceProps {
  onClick?: () => void;
  isFullReact?: boolean;
}

const ICON_MAP: Record<string, IconGlyph> = {
  flight: 'IconAirplane',
  accommodation: 'IconBed',
};

const StickyBar = styled.div<{ $isFullReact: boolean }>`
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;

  bottom: 0;
  left: 0;
  z-index: 50;
  padding: 12px;
  width: 100%;

  background: white;
  box-shadow: ${tk('shadow_md_top')};

  > * {
    flex: 1;
    display: flex;
  }

  ${Button} {
    height: fit-content;
  }

  ${({ $isFullReact }) => css`
    @media ${tk($isFullReact ? 'mq_desktop_lg' : 'mq_tablet')} {
      display: none;
    }
  `}
`;

const InfoWrapper = styled(SpaceBetween)`
  flex: 2;
`;

const Route = styled.div`
  display: flex;
  color: ${tk('color_semantic_text_subdued')};
`;

const Filters = styled(Typography)`
  color: ${tk('color_semantic_text_subdued')};
`;

const Services = styled.div`
  display: flex;
  flex-wrap: wrap;

  color: ${tk('color_semantic_text_subdued')};

  & > *:not(:first-child)::before {
    content: '+';
    padding-right: ${tk('spacing_2xs')};
    padding-left: ${tk('spacing_2xs')};
  }
`;

const CampaignImg = styled.img`
  height: 32px;
  width: auto;
`;

const ImageWrapper = styled.div`
  margin-bottom: ${tk('spacing_md')};
`;

const Wrapper = styled.div<{ $isFullReact: boolean }>`
  display: flex;
  background-color: ${tk('color_semantic_bg')};
  margin-bottom: ${tk('spacing_md')};

  > :nth-last-child(2) {
    flex: 1;
    display: flex;
  }

  > * {
    justify-content: flex-end;
  }

  > ${Button} {
    display: none;
  }

  ${({ $isFullReact }) => {
    if ($isFullReact) {
      return css`
        @media ${tk('mq_desktop_lg')} {
          flex-direction: column;
          align-items: center;
          padding: ${tk('spacing_md')};
          margin-top: ${tk('spacing_md')};
          margin-bottom: unset;
          box-shadow:
            0px 0px 2px 0px rgba(0, 0, 0, 0.06),
            0px 1px 4px 0px rgba(0, 0, 0, 0.08),
            0px 2px 8px 0px rgba(0, 0, 0, 0.1);

          > ${Button} {
            display: block;
            width: 100%;
          }

          * {
            justify-content: center;
            text-align: center;
          }

          ${PriceBox} {
            ${PriceBox.modAlign('center')};
            margin-top: ${tk('spacing_md')};
            margin-bottom: ${tk('spacing_md')};
          }
        }
      `;
    }
    return css`
      @media ${tk('mq_tablet')} {
        flex-direction: column;
        align-items: center;
        padding: ${tk('spacing_md')};
        margin-top: ${tk('spacing_lg')};
        margin-bottom: unset;
        box-shadow:
          0px 0px 2px 0px rgba(0, 0, 0, 0.06),
          0px 1px 4px 0px rgba(0, 0, 0, 0.08),
          0px 2px 8px 0px rgba(0, 0, 0, 0.1);

        > ${Button} {
          display: block;
          width: 100%;
        }

        * {
          justify-content: center;
          text-align: center;
        }

        ${PriceBox} {
          ${PriceBox.modAlign('center')};
          margin-top: ${tk('spacing_md')};
          margin-bottom: ${tk('spacing_md')};
        }
      }
    `;
  }}
`;

const PaymentMethodHighlighted = styled(Typography)`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 4px;
  margin-bottom: 0;
  bottom: 15px;
  @media ${tk('mq_desktop_lg')} {
    bottom: -20px;
  }
`;
const PaymentMethodImg = styled.img`
  height: 24px;
  width: auto;
`;
// eslint-disable-next-line @typescript-eslint/no-empty-function -- onClick is optional due to hybrid approach
const PromotedPrice = ({ onClick = () => {}, isFullReact = false }: PromotedPriceProps) => {
  const { data, isError } = usePromotedPrice();
  const { isAccommodationAvailable, isFetching } = useAccommodationAvailability();
  useSendAdditionalDetails();

  if (!isAccommodationAvailable && !isFetching) {
    return <AlertServiceSidebar />;
  }

  if (isError) {
    return <PromotedPriceSkeleton error onClick={onClick} isFullReact={isFullReact} />;
  }

  if (!data) {
    return <PromotedPriceSkeleton onClick={onClick} isFullReact={isFullReact} />;
  }

  const { filters, departureAirport, departureDate, services, campaignImage, paymentMethod } = data;

  return (
    <>
      <div>
        <Wrapper data-qa="promoted-price" $isFullReact={isFullReact}>
          <InfoWrapper direction="y" spacing={tk('spacing_2xs')}>
            {campaignImage && (
              <ImageWrapper>
                <CampaignImg src={campaignImage.src} alt={campaignImage.alt} />
              </ImageWrapper>
            )}
            <Services>
              {services.map((service) => (
                <Icon key={service} glyph={ICON_MAP[service]} size="md" />
              ))}
            </Services>
            <Typography $variant="body-sm">{departureDate.formatted}</Typography>
            <Filters $variant="body-sm">{filters.join(', ')}</Filters>
            {departureAirport && (
              <Route>
                <Icon glyph="IconAirplaneDeparture" size="sm" />
                <Typography as="div" $variant="body-sm">
                  {departureAirport}
                </Typography>
              </Route>
            )}
          </InfoWrapper>
          <PriceInfoBox size="large" align="end" />
          <PromotedPriceButton onClick={onClick} />
        </Wrapper>
        {paymentMethod?.icon && paymentMethod?.text && (
          <PaymentMethodHighlighted as="div">
            <PaymentMethodImg src={paymentMethod?.icon} alt={paymentMethod?.text} />
            {paymentMethod?.text}
          </PaymentMethodHighlighted>
        )}
      </div>
      <StickyBar $isFullReact={isFullReact}>
        <PriceInfoBox size="small" align="start" />
        <PromotedPriceButton onClick={onClick} />
      </StickyBar>
    </>
  );
};

function PriceInfoBox({ size, align }: { size: PriceBoxSize; align: PriceBoxAlignment }) {
  const { data, participants } = usePromotedPrice();

  if (!data) {
    return null;
  }

  const { price, transportType, departureDate, mealplan, accommodationId, duration } = data;

  return (
    <PriceInfo
      accommodationId={accommodationId}
      departureDateRaw={departureDate.date}
      durationRaw={duration}
      mealPlan={mealplan}
      price={price.value}
      priceDiscounts={{
        discountPercentage: price.discountPercentage,
        originalPrice: price.originalValue,
      }}
      transportType={transportType}
      size={size}
      align={align}
      participants={participants}
    />
  );
}

const modLoadingOpacity = (): RuleSet<object> => css`
  & ${Typography},${Icon} {
    opacity: 0.4;
  }
`;
const modLoaderIcon = (): RuleSet<object> => css`
  ${ButtonLoaderIcon} {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -15px;
    margin-left: -15px;

    & svg {
      width: 30px;
      height: 30px;
      color: ${tk('color_palette_grey_900')};
    }
  }
`;
const SkeletonWrapper = styled.div<{ $isFullReact: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${tk('spacing_md')};
  background-color: ${tk('color_semantic_bg')};

  ${Button} {
    display: none;
  }

  ${({ $isFullReact }) => css`
    @media ${tk($isFullReact ? 'mq_desktop_lg' : 'mq_tablet')} {
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      min-height: 235px;

      margin-top: ${tk('spacing_lg')};

      box-shadow:
        0px 0px 2px 0px rgba(0, 0, 0, 0.06),
        0px 1px 4px 0px rgba(0, 0, 0, 0.08),
        0px 2px 8px 0px rgba(0, 0, 0, 0.1);

      > ${Button} {
        display: block;
        width: 100%;
      }
    }
  `}
`;

const SkeletonBody = styled.div<{ loading: boolean; $isFullReact: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
  position: relative;

  ${({ $isFullReact }) => css`
    @media ${tk($isFullReact ? 'mq_desktop_lg' : 'mq_tablet')} {
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
    }
  `}

  ${({ loading }) => loading && modLoadingOpacity()}
  ${() => modLoaderIcon()}
`;

const SkeletonPrice = styled.div`
  width: 100px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: ${tk('spacing_md')};
`;

const PriceText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100px;
`;

const PriceSupportText = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`;
const InfoIcon = styled(Icon)`
  color: ${tk('color_semantic_text_strong')};
`;
const SkeletonStickyBarBody = styled.div<{ loading: boolean }>`
  position: relative;
  ${({ loading }) => loading && modLoadingOpacity()}
  ${() => modLoaderIcon()}
`;

const ErrorContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  & :first-child {
    padding-top: 0;
  }

  & > ${Button} {
    display: inline-flex !important;
  }
`;
function PromotedPriceSkeleton({
  error = false,
  onClick,
  isFullReact,
}: {
  error?: boolean;
  onClick: () => void;
  isFullReact: boolean | false;
}) {
  const intl = useIntl();
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <SkeletonWrapper $isFullReact={isFullReact}>
        {error && (
          <ErrorContentWrapper>
            <UnavailableContentDialog
              title={intl.formatMessage({ id: 'prices_promoted-price-error-title' })}
              message={intl.formatMessage({ id: 'prices_promoted-price-error-message' })}
            />
            <PromotedPriceButton />
          </ErrorContentWrapper>
        )}
        {!error && (
          <>
            <SkeletonBody loading={loading} $isFullReact={isFullReact}>
              {loading && <ButtonLoaderIcon />}
              <SkeletonPrice>
                <PriceSupportText $variant="body-xs">
                  {intl.formatMessage({ id: 'price_box_price_text' })}
                </PriceSupportText>
                <PriceText>
                  <Typography $variant="heading-2xl">{intl.formatCurrency(0, true)[0]}</Typography>
                  <InfoIcon glyph="IconInformationCircle" size="sm" />
                </PriceText>
              </SkeletonPrice>
            </SkeletonBody>
            <PromotedPriceButton onClick={onClick} />{' '}
          </>
        )}
      </SkeletonWrapper>
      <StickyBar $isFullReact={isFullReact}>
        <SkeletonStickyBarBody loading={loading}>
          {loading && !error && <ButtonLoaderIcon />}
          {!error && (
            <SkeletonPrice>
              <PriceSupportText $variant="body-xs">
                {intl.formatMessage({ id: 'price_box_price_text' })}
              </PriceSupportText>
              <PriceText>
                <Typography $variant="heading-2xl">{intl.formatCurrency(0, true)[0]}</Typography>
                <InfoIcon glyph="IconInformationCircle" size="sm" />
              </PriceText>
            </SkeletonPrice>
          )}
        </SkeletonStickyBarBody>
        <PromotedPriceButton onClick={onClick} />
      </StickyBar>
    </>
  );
}

function PromotedPriceButton({ onClick }: { onClick?: () => void }) {
  const { setShowPriceTable } = usePricesStore();
  const intl = useIntl();
  const onClickHandler = () => {
    const currentUrl = window.location.href;
    const url = currentUrl.split('#')[0];
    if (onClick) {
      onClick();
    }
    window.location.href = `${url}#acco-tab-36a34a40-1d2d-4c85-807c-3656ac5c0996`;
    // TODO: work around to fix departure date bug MN-5511
    setShowPriceTable(true);
  };

  return (
    <Button data-qa="promoted-price-btn" onClick={onClickHandler}>
      {intl.formatMessage({ id: 'prices_promoted_price_button-text' })}
    </Button>
  );
}

export default PromotedPrice;
